import React, { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import dateformat from "dateformat";
import ReactHtmlParser from "react-html-parser";

import Link from "../../Link";
import styles from "./styles";

import { calculateDistance, formatDateToNames, geocode, getDistanceMatrix, getMonthNumberFromName, validPostcode } from "../../Calendar/helpers";
import Months from "../../Calendar/Months";
import { useEffect } from "react";

const useStyles = makeStyles((theme) => styles(theme));

export default function Calendar(props) {
  const now = new Date();
  const currentMonth = dateformat(now, "m");
  const currentYear = Number(dateformat(now, "yyyy"));

  const [selectedMonth, setMonth] = useState(currentMonth);
  const [selectedYear, setYear] = useState(currentYear);
  const [searching, setSearching] = useState(false);
  const [selectedPostcode, setSelectedPostcode] = useState("");
  const [formErrors, setFormErrors] = useState([]);
  const [geocodingResult, setGeocodingResult] = useState(null);
  const [nearbyCourseDates, setNearbyCourseDates] = useState(props.courseDates || []);
  const classes = useStyles();

  useEffect(() => {
    const postcodeErrorMessage = "Please enter a valid UK postcode";
    if (selectedPostcode.length > 0) {
      let errors = formErrors
      if (selectedPostcode && selectedPostcode.length > 0 && !validPostcode(selectedPostcode)) {
        if (errors.indexOf(postcodeErrorMessage) === -1) {
          errors.push(postcodeErrorMessage);
        }
      } else {
        const postcodeErrorIndex = errors.indexOf(postcodeErrorMessage);
        if (postcodeErrorIndex !== -1) {
          errors.splice(postcodeErrorIndex, 1);
        }
      }
      const geocodingErrorMessage = "Postcode not found, please check and try again";
      const geocodingErrorIndex = formErrors.indexOf(geocodingErrorMessage);
      if (geocodingErrorIndex > -1) {
        errors.splice(geocodingErrorIndex, 1);
      }
      setFormErrors(errors);
      if (errors.length > 0) {
        return;
      }

      const handler = setTimeout(() => {
        geocode(selectedPostcode)
          .then((geocodingResult) => {
            if (geocodingResult && geocodingResult.latitude && geocodingResult.longitude) {
              setGeocodingResult(geocodingResult);
            } else {
              setFormErrors([...formErrors, geocodingErrorMessage]);
              setGeocodingResult(null);
            }
          })
          .catch((err) => {
            console.log('geocode error', err);
          });
      }, 1000);
      return () => clearTimeout(handler);
    } else {
      setGeocodingResult(null);
      setFormErrors([]);
    }
  }, [selectedPostcode]);

  useEffect(() => {
    if (geocodingResult) {
      fetchDistances(props.courseDates);
    } else {
      setNearbyCourseDates(props.courseDates);
    }
  }, [geocodingResult]);

  async function fetchDistances(courseDates) {
    // get lat/lon for the postcode
    if (!geocodingResult || !geocodingResult.latitude || !geocodingResult.longitude) {
      return;
    }
    let courseDatesWithinRange = props.courseDates.map((courseDate) => {
      let value = { ...courseDate, courseData: { ...courseDate.courseData } };
      if (courseDate.courseData.latitude && courseDate.courseData.longitude) {
        value.distance = calculateDistance(geocodingResult.latitude, geocodingResult.longitude, courseDate.courseData.latitude, courseDate.courseData.longitude);
      } else {
        value.courseData.distance = null;
      }
      return value;
    })
    .filter((courseDate) => {
      return courseDate.distance && courseDate.distance <= 90;
    }).map((courseDate) => {
      courseDate.latitude = courseDate.courseData.latitude;
      courseDate.longitude = courseDate.courseData.longitude;
      return courseDate;
    });
    if (courseDatesWithinRange.length > 0) {
      courseDatesWithinRange = await getDistanceMatrix(geocodingResult, courseDatesWithinRange);
      courseDatesWithinRange = courseDatesWithinRange.filter(courseDate => {
        return courseDate.roadDistance && courseDate.roadDistance <= 90;
      });
    }

    setNearbyCourseDates(courseDatesWithinRange);
  }

  function handleMonth(e) {
    if (e) e.preventDefault();
    if (e.target.type !== "radio") return false;
    const year = Number(e.target.getAttribute("year"));

    setYear(year);
    setMonth(e.target.value);
  }

  function renderCourseDates() {
    const output = nearbyCourseDates
      .map((course) => {
        const { id, uri } = course;

        if (!course.courseData.course) return null;
        const {
          startDate,
          endDate,
          place,
          seatsBooked,
          seatsAvailable,
        } = course.courseData;
        const startMonth = Number(startDate.substr(4, 2));
        const endMonth = Number(endDate.substr(4, 2));
        const startYear = Number(startDate.substr(0, 4));
        const endYear = Number(endDate.substr(0, 4));
        const fullyBooked = seatsAvailable !== null && seatsAvailable - seatsBooked === 0;

        const databaseId = course.courseData.course.databaseId;

        if (startYear !== selectedYear && endYear !== selectedYear) return null;
        if (databaseId !== props.databaseId) return null;
        if ( !( selectedMonth >= startMonth && selectedMonth <= endMonth ) ) return null;

        return (
          <div
            key={id}
            className={
              seatsAvailable !== null && seatsAvailable - seatsBooked === 0
                ? "course-item full"
                : "course-item"
            }
          >
            <div className="dates">
              <span className="title">Dates</span>
              <span>
                {ReactHtmlParser(
                  formatDateToNames(startDate)
                    .replace("[]", "<span>")
                    .replace("[/]", "</span>")
                )}{" "}
                -{" "}
                {ReactHtmlParser(
                  formatDateToNames(endDate)
                    .replace("[]", "<span>")
                    .replace("[/]", "</span>")
                )}
              </span>
            </div>

            <div className="location">
              <span className="title">Location</span>
              <span>{place?.title}</span>
            </div>
            {(course.roadDistance || course.distance) && (
              <div className="distance">
                <span className="title">Distance</span>
                <span>{Math.round((course.roadDistance ?? course.distance) * 100) / 100} miles</span>
              </div>
            )}

            <div className="spaces">
              {seatsAvailable !== null && (
                <>
                  <span className="title">Spaces</span>
                  <span>
                    <strong className={seatsAvailable - seatsBooked === 0 ? "full" : ""}>
                      {seatsAvailable - seatsBooked}
                    </strong>
                    /{seatsAvailable}
                  </span>
                </>
              )}
            </div>
            <div className="link">
              <Link
                className="enquiry"
                to={fullyBooked ? "" : "/course-enquiry"}
                state={{
                  courseId: course.courseData.course.id,
                  dateId: id,
                  startDate,
                  endDate,
                }}
              >
                {fullyBooked ? "Fully Booked" : "Enquire"}
              </Link>
            </div>
          </div>
        );
      })
      .filter((courseDates) => courseDates);

    if (output.length) {
      return output;
    } else if (!searching) {
      return (
        <p>
          We currently don't have any courses scheduled for this month{geocodingResult ? " within 90 miles of your postcode" : ""}. Please
          check other months{geocodingResult ? ", change your postcode, " : " "} or{" "}
          <Link to="/course-enquiry" state={{ courseId: props.id }}>
            get in touch
          </Link>
          .
        </p>
      );
    } else {
      return <p>Loading...</p>;
    }
  }

  return (
    <>
      <div className={classes.courseFilters}>
        <div className="postcode">
          <label htmlFor="postcode">Postcode</label>
          <input
            type="text"
            name="postcode"
            value={selectedPostcode}
            onChange={(e) => setSelectedPostcode(e.target.value)}
            placeholder="Postcode"
          />
        </div>
        <div className="errors">
          { formErrors.map((error, index) => <div key={index} className="error">{error}</div>) }
        </div>
        {geocodingResult && (
          <div className="distance-notice">
            <span>Showing results within 90 miles {geocodingResult.full_address && `of "${geocodingResult?.full_address}"`}</span>
          </div>
        )}
      </div>
      <div className={classes.availListing}>
        <Months
          handleMonth={handleMonth}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
        />

        <div className={`listing-wrap ${geocodingResult ? 'with-distance' : ''}`}>
          <div className="course-titles">
            <div className="dates">Dates</div>
            <div className="location">Location</div>
            {geocodingResult && (
              <div className="distance">Distance</div>
            )}
            <div className="spaces">Spaces Left</div>
          </div>
          <div className="course-listing">{renderCourseDates()}</div>
        </div>
      </div>
    </>
  );
}
